import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import {
  isDriveTimePolygon, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { getOrSet } from '~/_shared/utils/collections/collections';
import { openMapTool } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { setType } from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { useTranslation } from '../../../_shared/utils/hooks';
import { useProximities } from '../../../proximity/useProximities';
import { removeProximity } from '../../../store/mapSettings/proximity/mapSettingsProximity.actionCreators';
import {
  hideProximity, showProximity,
} from '../../../store/mapSettings/toolsState/visibility/visibility.actionCreators';
import { useHiddenProximityIdsSelector } from '../../../store/mapSettings/toolsState/visibility/visibility.selectors';
import { useMapIdSelector } from '../../../store/selectors/useMapIdSelector';
import {
  createMapStatusBarFromProximity, createMapStatusBarItemFromCollapsedProximities,
} from '../item/mapStatusBarItem.factory';
import {
  type MapStatusBarItem, MapStatusBarItemCollapsedState,
} from '../mapStatusBarItem.type';

// Proximities of the same type are collapsed under a single summary item once there are at least this many of them.
export const PROXIMITY_COLLAPSE_THRESHOLD = 3;

export const useProximityStatusBarItems = () => {
  const proximities = useProximities();
  const hiddenProximityIds = useHiddenProximityIdsSelector();
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  // Types missing here are collapsed, so every type starts collapsed.
  const [expandedTypes, setExpandedTypes] = useState<ReadonlySet<ProximityType>>(() => new Set());

  useEffect(() => {
    setExpandedTypes(new Set());
  }, [mapId]);

  const onCollapse = useCallback((proximityType: ProximityType, collapsedState: MapStatusBarItemCollapsedState) => {
    setExpandedTypes(currentExpandedTypes => {
      const newExpandedTypes = new Set(currentExpandedTypes);

      if (collapsedState === MapStatusBarItemCollapsedState.Expanded) {
        newExpandedTypes.add(proximityType);
      }
      else {
        newExpandedTypes.delete(proximityType);
      }

      return newExpandedTypes;
    });
  }, []);

  return useMemo<MapStatusBarItem[]>(() => {
    if (!mapId) {
      return [];
    }

    const displayedProximities = proximities
      .filter(proximity => !isDriveTimePolygon(proximity) || proximity.data.paths);

    const items = displayedProximities
      .map((proximity, index) => createMapStatusBarFromProximity(
        {
          t, proximity,
          isProximityVisible: !hiddenProximityIds.includes(proximity.id),
          index: index + 1,
          onRemove: () => dispatch(removeProximity(proximity, mapId)),
          onVisibilityChange: (newVisibility) => {
            if (newVisibility) {
              dispatch(showProximity(proximity.id));
            }
            else {
              dispatch(hideProximity(proximity.id));
            }
          },
          onClick: () => {
            dispatch(setType(isDriveTimePolygon(proximity) ? ProximityType.DriveTimePolygon : ProximityType.DistanceRadius));
            dispatch(openMapTool(MapTool.Proximity));
          },
        }));

    const proximitiesByType = new Map<ProximityType, Proximity[]>();
    displayedProximities.forEach(proximity =>
      getOrSet(proximitiesByType, proximity.type, () => []).push(proximity)
    );

    const createCollapsedItem = (proximityType: ProximityType, typeProximities: Proximity[]) => {
      const visibleCount = typeProximities.filter(proximity => !hiddenProximityIds.includes(proximity.id)).length;

      return createMapStatusBarItemFromCollapsedProximities({
        t,
        proximityType,
        count: typeProximities.length,
        // The eye reflects the majority of the collapsed items.
        isVisible: visibleCount * 2 > typeProximities.length,
        collapsedState: expandedTypes.has(proximityType)
          ? MapStatusBarItemCollapsedState.Expanded
          : MapStatusBarItemCollapsedState.Collapsed,
        onVisibilityChange: (newVisibility) => typeProximities.forEach(proximity =>
          dispatch(newVisibility ? showProximity(proximity.id) : hideProximity(proximity.id))
        ),
        onClick: () => {
          dispatch(setType(proximityType));
          dispatch(openMapTool(MapTool.Proximity));
        },
        onCollapse: (collapsedState) => onCollapse(proximityType, collapsedState),
      });
    };

    const typesWithCollapsedItem = new Set<ProximityType>();

    return displayedProximities.reduce<MapStatusBarItem[]>((result, proximity, index) => {
      const item = items[index];
      if (!item) {
        return result;
      }

      const typeProximities = proximitiesByType.get(proximity.type) ?? [];
      if (typeProximities.length < PROXIMITY_COLLAPSE_THRESHOLD) {
        result.push(item);
        return result;
      }

      if (!typesWithCollapsedItem.has(proximity.type)) {
        typesWithCollapsedItem.add(proximity.type);
        result.push(createCollapsedItem(proximity.type, typeProximities));
      }

      if (expandedTypes.has(proximity.type)) {
        result.push(item);
      }

      return result;
    }, []);
  }, [t, dispatch, proximities, hiddenProximityIds, mapId, expandedTypes, onCollapse]);
};
